import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '@/shared/config/firebase';
import { toast } from 'react-hot-toast';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { CustomerService } from '@/services/firebase/customer-service';
import { CustomerRepository } from '@/services/firebase/customer-repository';
import { Provider } from '@/models/customer';
import { analyticsService } from '@/services/analytics';
import { syncUserClaims, refreshAuthToken, requestImpersonationToken, requestStopImpersonationToken, getImpersonationInfo, ImpersonationInfo } from '@/services/auth-claims-service';

interface AuthContextType {
  currentUser: User | null;
  customer: any | null; // Profil complet de la table customer
  isAdmin: boolean;
  isAdminForOrganization: (organizationId: string) => boolean;
  hasAccessToOrganization: (organizationId: string) => boolean;
  getUserOrganizations: () => Promise<void>;
  affiliations: any[];
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  customerLoading: boolean;
  // Nouvelles fonctionnalités de permissions
  permissionsLoading: boolean;
  permissionsInitialized: boolean;
  getUserRole: (organizationId: string) => string | null;
  refreshPermissions: () => Promise<void>;
  // Permissions granulaires
  canViewOrders: (organizationId: string) => boolean;
  canManageOrders: (organizationId: string) => boolean;
  canManageConfiguration: (organizationId: string) => boolean;
  canViewAnalytics: (organizationId: string) => boolean;
  canManageUsers: (organizationId: string) => boolean;
  refreshCustomer: () => Promise<void>;
  // Impersonation super admin
  impersonation: ImpersonationInfo;
  impersonateUser: (email: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook de compatibilité pour le passage de CustomerContext vers AuthContext
export const useCustomer = () => {
  const { customer, customerLoading: isLoading, refreshCustomer } = useAuth();
  return { customer, customerId: customer?.id || null, isLoading, refreshCustomer };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationInfo>({ isImpersonating: false });

  // Charger le customer document depuis Firestore
  const loadCustomer = useCallback(async () => {
    if (!currentUser?.uid) {
      setCustomer(null);
      setCustomerLoading(false);
      return;
    }

    setCustomerLoading(true);
    try {
      const foundCustomer = await CustomerRepository.findByFirebaseUid(currentUser.uid);
      setCustomer(foundCustomer || null);
      console.log('👤 AuthProvider - Customer chargé:', {
        uid: currentUser.uid,
        customerId: foundCustomer?.id,
        email: foundCustomer?.email
      });
    } catch (err) {
      console.error('❌ AuthProvider - Erreur chargement customer:', err);
      setCustomer(null);
    } finally {
      setCustomerLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  const refreshCustomer = async () => {
    await loadCustomer();
  };

  // Utiliser le nouveau système de permissions unifié avec les données du customer
  const {
    isLoading: permissionsLoading,
    initialized: permissionsInitialized,
    userAffiliations: affiliations,
    isAdminForOrganization: checkIsAdminForOrganization,
    hasAccessToOrganization: checkHasAccessToOrganization,
    getUserRole: getUserRoleFromAffiliations,
    canViewOrders: checkCanViewOrders,
    canManageOrders: checkCanManageOrders,
    canManageConfiguration: checkCanManageConfiguration,
    canViewAnalytics: checkCanViewAnalytics,
    canManageUsers: checkCanManageUsers,
    refreshPermissions
  } = usePermissions(
    currentUser,
    customer?.id || null,
    customer?.isSuperAdmin || false
  );

  // isAdmin est maintenant dérivé du customer
  const isAdmin = customer?.isSuperAdmin || false;


  // Vérifier si l'utilisateur est admin pour une organisation spécifique
  const isAdminForOrganization = (organizationId: string): boolean => {
    // ✅ Si super admin, accès total à toutes les organisations
    if (isAdmin) return true;

    // Sinon, utiliser le système de permissions unifié (affiliations)
    return checkIsAdminForOrganization(organizationId);
  };

  // Vérifier si l'utilisateur a accès à une organisation
  const hasAccessToOrganization = (organizationId: string): boolean => {
    // ✅ Si super admin, accès total à toutes les organisations
    if (isAdmin) return true;

    // Sinon, utiliser le système de permissions unifié (affiliations)
    return checkHasAccessToOrganization(organizationId);
  };

  // Récupérer le rôle de l'utilisateur pour une organisation
  const getUserRole = (organizationId: string): string | null => {
    // ✅ Si super admin, retourner 'owner' pour toutes les organisations
    if (isAdmin) return 'owner';

    // Sinon, utiliser le système de permissions unifié (affiliations)
    return getUserRoleFromAffiliations(organizationId) || null;
  };

  // Dummy implementation for backward compatibility
  const getUserOrganizations = async () => {
    console.warn('⚠️ getUserOrganizations est déprécié');
  };

  // Wrappers pour les permissions granulaires (avec support super admin)
  const canViewOrders = (organizationId: string): boolean => {
    if (isAdmin) return true;
    return checkCanViewOrders(organizationId);
  };

  const canManageOrders = (organizationId: string): boolean => {
    if (isAdmin) return true;
    return checkCanManageOrders(organizationId);
  };

  const canManageConfiguration = (organizationId: string): boolean => {
    if (isAdmin) return true;
    return checkCanManageConfiguration(organizationId);
  };

  const canViewAnalytics = (organizationId: string): boolean => {
    if (isAdmin) return true;
    return checkCanViewAnalytics(organizationId);
  };

  const canManageUsers = (organizationId: string): boolean => {
    if (isAdmin) return true;
    return checkCanManageUsers(organizationId);
  };

  /**
   * Met à jour les providers d'un customer à partir des providerData Firebase Auth
   */
  const updateCustomerProviders = async (user: User) => {
    try {

      // Extraire les providers depuis Firebase Auth
      const providers: Provider[] = [];

      if (user.providerData && user.providerData.length > 0) {
        user.providerData.forEach((providerData) => {
          let providerType: 'google' | 'facebook' | 'apple' | 'email' = 'email';

          if (providerData.providerId === 'google.com') {
            providerType = 'google';
          } else if (providerData.providerId === 'facebook.com') {
            providerType = 'facebook';
          } else if (providerData.providerId === 'apple.com') {
            providerType = 'apple';
          } else if (providerData.providerId === 'password') {
            providerType = 'email';
          }

          // Créer le provider en enlevant les valeurs undefined
          const provider: Provider = {
            type: providerType,
            providerUid: providerData.uid || user.uid,
          };

          if (providerData.email) {
            provider.email = providerData.email;
          }
          if (providerData.displayName) {
            provider.displayName = providerData.displayName;
          }
          if (providerData.photoURL) {
            provider.photoURL = providerData.photoURL;
          }

          providers.push(provider);
        });
      } else {
        // Provider email par défaut
        const provider: Provider = {
          type: 'email',
          providerUid: user.uid,
        };

        if (user.email) {
          provider.email = user.email;
        }
        if (user.displayName) {
          provider.displayName = user.displayName;
        }
        if (user.photoURL) {
          provider.photoURL = user.photoURL;
        }

        providers.push(provider);
      }

      // Utiliser CustomerService qui gère findOrCreate (anti-doublon)
      const customer = await CustomerService.findOrCreateCustomer(
        user.email || '',
        user.uid
      );

      if (customer) {
        // Fusionner les providers (éviter les doublons)
        const existingProviderTypes = new Set(customer.providers.map(p => p.type));
        const newProviders = providers.filter(p => !existingProviderTypes.has(p.type));
        const updatedProviders = customer.providers.map(existingProvider => {
          const newProvider = providers.find(p => p.type === existingProvider.type);
          return newProvider || existingProvider;
        });

        if (newProviders.length > 0 || updatedProviders.length !== customer.providers.length) {
          await CustomerRepository.update(customer.id, {
            providers: [...updatedProviders, ...newProviders],
          });
          console.log('✅ Providers mis à jour:', customer.id);
        }
      }
    } catch (error) {
      console.error('⚠️ Erreur lors de la mise à jour des providers:', error);
      // Ne pas bloquer si la mise à jour échoue
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user);
          toast.success('Nouveau lien de vérification envoyé. Vérifiez votre boîte mail.');
        } catch (verificationError) {
          console.error('⚠️ Impossible de renvoyer l’email de vérification:', verificationError);
          toast.error('Email non vérifié. Impossible de renvoyer le lien pour l’instant.');
        } finally {
          await signOut(auth);
        }
        const verificationError: any = new Error('Email non vérifié');
        verificationError.code = 'auth/email-not-verified';
        throw verificationError;
      }

      // Mettre à jour les providers du customer lors de la connexion
      await updateCustomerProviders(user);

      // Synchroniser les custom claims (isSuperAdmin) pour les Firestore Rules
      await syncUserClaims(user.uid);

      // Rafraîchir le token pour obtenir les nouveaux claims
      await refreshAuthToken(user);

      // Analytics - l'événement login sera aussi enregistré dans onAuthStateChanged
      // mais on peut ajouter des détails supplémentaires ici si nécessaire

      toast.success('Connexion réussie!');
    } catch (error: any) {
      console.error('Login error:', error);
      let message = 'Erreur de connexion';

      switch (error.code) {
        case 'auth/user-not-found':
          message = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/wrong-password':
          message = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          message = 'Email invalide';
          break;
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
          message = 'Email ou mot de passe incorrect';
          break;
        case 'auth/too-many-requests':
          message = 'Trop de tentatives. Réessayez plus tard';
          break;
        case 'auth/network-request-failed':
          message = 'Erreur de connexion réseau';
          break;
        default:
          // Handle generic INVALID_LOGIN_CREDENTIALS from error message
          if (error.message?.includes('INVALID_LOGIN_CREDENTIALS')) {
            message = 'Email ou mot de passe incorrect';
          } else {
            message = 'Erreur de connexion';
          }
      }

      toast.error(message);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      // Créer le compte Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      console.log('✅ Compte Firebase Auth créé:', user.uid);

      // Extraire les informations des providers Firebase Auth
      const providers: Provider[] = [];

      if (user.providerData && user.providerData.length > 0) {
        user.providerData.forEach((providerData) => {
          // Mapper le providerId Firebase vers notre type Provider
          let providerType: 'google' | 'facebook' | 'apple' | 'email' = 'email';

          if (providerData.providerId === 'google.com') {
            providerType = 'google';
          } else if (providerData.providerId === 'facebook.com') {
            providerType = 'facebook';
          } else if (providerData.providerId === 'apple.com') {
            providerType = 'apple';
          } else if (providerData.providerId === 'password') {
            providerType = 'email';
          }

          // Créer le provider en enlevant les valeurs undefined
          const provider: Provider = {
            type: providerType,
            providerUid: providerData.uid || user.uid, // Utiliser l'UID du provider ou l'UID Firebase
          };

          if (providerData.email) {
            provider.email = providerData.email;
          }
          if (providerData.displayName) {
            provider.displayName = providerData.displayName;
          }
          if (providerData.photoURL) {
            provider.photoURL = providerData.photoURL;
          }

          providers.push(provider);
        });
      } else {
        // Si pas de providerData, créer un provider email par défaut
        const provider: Provider = {
          type: 'email',
          providerUid: user.uid,
        };

        if (user.email || email) {
          provider.email = user.email || email;
        }
        if (user.displayName) {
          provider.displayName = user.displayName;
        }
        if (user.photoURL) {
          provider.photoURL = user.photoURL;
        }

        providers.push(provider);
      }

      // Créer le customer avec les providers
      try {
        const customer = await CustomerService.findOrCreateCustomer(
          user.email || email,
          user.uid
        );

        // Fusionner les providers existants avec les nouveaux (éviter les doublons)
        const existingProviderTypes = new Set(customer.providers.map(p => p.type));
        const newProviders = providers.filter(p => !existingProviderTypes.has(p.type));

        if (newProviders.length > 0) {
          await CustomerRepository.update(customer.id, {
            providers: [...customer.providers, ...newProviders],
          });
          console.log('✅ Providers ajoutés au customer existant:', customer.id);
        } else {
          const updatedProviders = customer.providers.map(existingProvider => {
            const newProvider = providers.find(p => p.type === existingProvider.type);
            return newProvider || existingProvider;
          });

          await CustomerRepository.update(customer.id, {
            providers: updatedProviders,
          });
          console.log('✅ Providers mis à jour pour le customer existant:', customer.id);
        }
      } catch (customerError) {
        console.error('⚠️ Erreur lors de la création/mise à jour du customer:', customerError);
        // Ne pas bloquer l'inscription si la création du customer échoue
      }

      try {
        await sendEmailVerification(user);
        toast.success('Compte créé ! Vérifiez votre email pour activer votre accès.');
      } catch (verificationError) {
        console.error('⚠️ Échec de l’envoi de l’email de vérification:', verificationError);
        toast.success('Compte créé. Impossible d’envoyer l’email de vérification, veuillez réessayer plus tard.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      let message = 'Erreur lors de la création du compte';

      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'Un compte existe déjà avec cet email';
          break;
        case 'auth/weak-password':
          message = 'Le mot de passe doit contenir au moins 6 caractères';
          break;
        case 'auth/invalid-email':
          message = 'Email invalide';
          break;
        default:
          message = 'Erreur lors de la création du compte';
      }

      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setImpersonation({ isImpersonating: false });
      toast.success('Déconnexion réussie');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Erreur lors de la déconnexion');
      throw error;
    }
  };

  const impersonateUser = async (email: string) => {
    if (!isAdmin) {
      toast.error('Seul un super admin peut impersonner un utilisateur');
      throw new Error('Unauthorized');
    }

    try {
      const { customToken, targetUser } = await requestImpersonationToken(email);
      if (!customToken) {
        throw new Error('Token d\'impersonation manquant');
      }

      const userCredential = await signInWithCustomToken(auth, customToken);
      await syncUserClaims(userCredential.user.uid);
      await refreshAuthToken(userCredential.user);

      const info = await getImpersonationInfo();
      setImpersonation(info);

      toast.success(`Connecté en tant que ${targetUser?.email || email}`);
    } catch (error: any) {
      console.error('Impersonation error:', error);
      toast.error(error.message || 'Erreur lors de l\'impersonation');
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const { customToken } = await requestStopImpersonationToken();
      if (!customToken) {
        throw new Error('Token de retour manquant');
      }

      const userCredential = await signInWithCustomToken(auth, customToken);
      await syncUserClaims(userCredential.user.uid);
      await refreshAuthToken(userCredential.user);

      setImpersonation({ isImpersonating: false });
      toast.success('Retour à votre compte super admin');
    } catch (error: any) {
      console.error('Stop impersonation error:', error);
      toast.error(error.message || 'Erreur lors du retour au compte super admin');
      throw error;
    }
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      // Mettre à jour les providers du customer quand l'utilisateur se connecte
      // Les organisations seront chargées automatiquement via usePermissions qui utilise le contexte customer
      if (user?.uid) {
        await updateCustomerProviders(user);

        const info = await getImpersonationInfo();
        setImpersonation(info);

        // Définir l'ID utilisateur dans Firebase Analytics
        analyticsService.setUserId(user.uid);
        analyticsService.setUserProperties({
          email: user.email || undefined,
          email_verified: user.emailVerified,
        });

        // Enregistrer l'événement de connexion
        analyticsService.logEvent('login', {
          method: 'email',
        });
      } else {
        // Utilisateur déconnecté
        setImpersonation({ isImpersonating: false });
        analyticsService.setUserId(null);
        analyticsService.logEvent('logout');
      }
    });

    return unsubscribe;
  }, []); // Enlever getUserOrganizationsLegacy des dépendances pour éviter les re-renders

  const value = {
    currentUser,
    customer,
    isAdmin,
    isAdminForOrganization,
    hasAccessToOrganization,
    getUserOrganizations,
    affiliations,
    login,
    register,
    logout,
    loading,
    customerLoading,
    // Nouvelles fonctionnalités de permissions
    permissionsLoading,
    permissionsInitialized,
    getUserRole,
    refreshPermissions,
    // Permissions granulaires
    canViewOrders,
    canManageOrders,
    canManageConfiguration,
    canViewAnalytics,
    canManageUsers,
    refreshCustomer,
    impersonation,
    impersonateUser,
    stopImpersonation,
    isImpersonating: impersonation.isImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 